import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Play,
  Video,
  Image,
  Megaphone,
  ShoppingBag,
  Globe,
  Users,
  Radio,
  Mail,
  MessageSquare,
  Phone,
  Shield,
  Link2,
  Trash2,
  CreditCard,
  Package,
  DollarSign,
  Send,
  Tv,
  Store,
  ArrowRight
} from "lucide-react";

// All service categories
const serviceCategories = [
  {
    id: "content-creation",
    title: "Content Creation",
    icon: <Video className="w-6 h-6" />,
    color: "from-primary to-purple-600",
    services: [
      { id: "text-to-video", name: "Text to Video", path: "/create", desc: "AI script to video" },
      { id: "text-to-image", name: "Text to Image", path: "/create", desc: "AI image generation" },
      { id: "image-to-video", name: "Image to Video", path: "/photo-to-video", desc: "Photo to sales video" },
      { id: "ad-maker", name: "Ad Maker", path: "/create", desc: "Commercial creation" },
      { id: "record", name: "Record Video", path: "/record", desc: "Webcam recording" }
    ]
  },
  {
    id: "marketplaces",
    title: "Marketplace Listings",
    icon: <ShoppingBag className="w-6 h-6" />,
    color: "from-accent-success to-emerald-600",
    services: [
      { id: "single-dashboard", name: "Single Sign-In Dashboard", path: "/broadcast", desc: "All platforms one login" },
      { id: "luxury", name: "Luxury Networks", path: "/broadcast", desc: "Chrono24, 1stDibs, Polygon" },
      { id: "free-markets", name: "Free Markets", path: "/broadcast", desc: "Craigslist, FB Marketplace" },
      { id: "ecommerce", name: "E-Commerce", path: "/broadcast", desc: "Amazon, eBay, Etsy, Shopify" }
    ]
  },
  {
    id: "social-platforms",
    title: "Social & Video Platforms",
    icon: <Globe className="w-6 h-6" />,
    color: "from-accent-neon to-blue-600",
    services: [
      { id: "youtube", name: "YouTube", path: "/broadcast", desc: "Video platform" },
      { id: "tiktok", name: "TikTok", path: "/broadcast", desc: "Short-form video" },
      { id: "instagram", name: "Instagram", path: "/broadcast", desc: "Photos & Reels" },
      { id: "facebook", name: "Facebook", path: "/broadcast", desc: "Social network" },
      { id: "linkedin", name: "LinkedIn", path: "/broadcast", desc: "Professional network" },
      { id: "twitter", name: "X/Twitter", path: "/broadcast", desc: "Microblogging" }
    ]
  },
  {
    id: "ad-networks",
    title: "Ad Networks & Streaming",
    icon: <Tv className="w-6 h-6" />,
    color: "from-accent-warning to-orange-600",
    services: [
      { id: "trade-desk", name: "The Trade Desk", path: "/broadcast", desc: "Programmatic DSP" },
      { id: "google-dv360", name: "Google DV360", path: "/broadcast", desc: "Display & Video" },
      { id: "roku", name: "Roku Ads", path: "/broadcast", desc: "Connected TV" },
      { id: "hulu", name: "Hulu/Streaming", path: "/broadcast", desc: "Streaming ads" },
      { id: "amazon-dsp", name: "Amazon DSP", path: "/broadcast", desc: "Amazon ecosystem" }
    ]
  },
  {
    id: "communication",
    title: "Communication Endpoints",
    icon: <MessageSquare className="w-6 h-6" />,
    color: "from-green-500 to-teal-600",
    services: [
      { id: "email", name: "Email", path: "/broadcast", desc: "Email campaigns" },
      { id: "sms", name: "SMS/Texting", path: "/broadcast", desc: "Text messages" },
      { id: "whatsapp", name: "WhatsApp", path: "/broadcast", desc: "WhatsApp messaging" },
      { id: "telegram", name: "Telegram", path: "/broadcast", desc: "Telegram messaging" },
      { id: "ptt", name: "Push to Talk", path: "/broadcast", desc: "Voice communication" },
      { id: "direct", name: "Direct Message", path: "/vidme", desc: "Direct delivery" }
    ]
  },
  {
    id: "verification",
    title: "Verification & Blockchain",
    icon: <Shield className="w-6 h-6" />,
    color: "from-indigo-500 to-violet-600",
    services: [
      { id: "selleverify", name: "SelleVerify", path: "/selleverify", desc: "Product authentication" },
      { id: "selleprint", name: "SellePrint", path: "/selleverify", desc: "Digital fingerprint" },
      { id: "blockchain", name: "Blockchain Record", path: "/selleverify", desc: "Immutable verification" },
      { id: "sellecertify", name: "SelleCertify", path: "/selleverify", desc: "Certification badge" }
    ]
  },
  {
    id: "payments",
    title: "Payments & Commerce",
    icon: <CreditCard className="w-6 h-6" />,
    color: "from-pink-500 to-rose-600",
    services: [
      { id: "buy-now", name: "Buy Now Button", path: "/broadcast", desc: "Instant purchase" },
      { id: "pay-later", name: "Pay Later (Klarna)", path: "/broadcast", desc: "Financing options" },
      { id: "shipping", name: "ShipStation", path: "/broadcast", desc: "Integrated shipping" },
      { id: "escrow", name: "Escrow Payments", path: "/broadcast", desc: "Secure transactions" }
    ]
  },
  {
    id: "management",
    title: "Account & Management",
    icon: <Users className="w-6 h-6" />,
    color: "from-slate-500 to-gray-600",
    services: [
      { id: "clients", name: "Client Accounts", path: "/dashboard", desc: "Sub-client management" },
      { id: "delete", name: "Delete Capabilities", path: "/library", desc: "Content deletion" },
      { id: "library", name: "Media Library", path: "/library", desc: "All your content" },
      { id: "analytics", name: "Analytics", path: "/dashboard", desc: "Performance tracking" }
    ]
  }
];

export default function ServicesHub() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) setUser(JSON.parse(stored));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent-neon flex items-center justify-center">
              <Play className="w-5 h-5 text-white fill-white" />
            </div>
            <span className="font-heading font-bold text-xl text-text-primary">SelleCast</span>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <span className="text-sm text-text-muted">
                  <span className="text-primary font-bold">{user.credits}</span> credits
                </span>
                <Button variant="outline" onClick={() => navigate("/dashboard")} className="border-border">
                  Dashboard
                </Button>
              </>
            ) : (
              <Button onClick={() => navigate("/login")} className="bg-primary text-white">
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-heading font-bold text-3xl text-text-primary mb-3">
            All Services Hub
          </h1>
          <p className="text-text-secondary max-w-2xl mx-auto">
            Complete platform for content creation, marketplace listings, ad networks, 
            communication endpoints, verification, and payments - all in one place.
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          <Button onClick={() => navigate("/vidme")} className="bg-primary hover:bg-primary-hover text-white h-auto py-4 flex-col">
            <Send className="w-5 h-5 mb-1" />
            <span>VidMe</span>
          </Button>
          <Button onClick={() => navigate("/create")} variant="outline" className="border-border h-auto py-4 flex-col">
            <Video className="w-5 h-5 mb-1" />
            <span>Create</span>
          </Button>
          <Button onClick={() => navigate("/broadcast")} variant="outline" className="border-border h-auto py-4 flex-col">
            <Megaphone className="w-5 h-5 mb-1" />
            <span>Broadcast</span>
          </Button>
          <Button onClick={() => navigate("/selleverify")} variant="outline" className="border-border h-auto py-4 flex-col">
            <Shield className="w-5 h-5 mb-1" />
            <span>Verify</span>
          </Button>
        </div>

        {/* Service Categories */}
        <div className="space-y-8">
          {serviceCategories.map((category, catIndex) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: catIndex * 0.1 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${category.color} flex items-center justify-center text-white`}>
                  {category.icon}
                </div>
                <h2 className="font-heading font-bold text-lg text-text-primary">
                  {category.title}
                </h2>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {category.services.map((service) => (
                  <Card
                    key={service.id}
                    onClick={() => navigate(service.path)}
                    className="cursor-pointer bg-background-paper border-border hover:border-primary/50 transition-all group"
                  >
                    <CardContent className="p-4">
                      <div className="font-medium text-sm text-text-primary group-hover:text-primary transition-colors">
                        {service.name}
                      </div>
                      <div className="text-xs text-text-muted mt-1">
                        {service.desc}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 p-6 rounded-2xl bg-gradient-to-br from-primary/10 to-accent-neon/10 border border-primary/20 text-center">
          <h3 className="font-heading font-bold text-lg text-text-primary mb-2">
            Ready to broadcast?
          </h3>
          <p className="text-sm text-text-secondary mb-4">
            Create content, verify products, and deploy to 30+ channels
          </p>
          <Button onClick={() => navigate("/vidme")} className="bg-primary hover:bg-primary-hover text-white">
            Start with VidMe
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </main>
    </div>
  );
}
